import { useCallback, useState } from 'react'
import { When } from 'react-if'
import { CircleCloseIcon } from '@/atoms/Icons/CircleCloseIcon'
import { Image } from '@/atoms/Image'
import { ExperimentVariation, GBFadeInExperiment } from '@/experimentation'
import { FloatingCallToActionType } from '@/services/CmsService/CallToAction'
import { GuildPermissions } from '@/services/GuildUserService'
import { buildCloudinaryImagePathFromContentfulObject, getCloudinaryImageUrl } from '@/utils/Cloudinary'
import { useSafeTrack } from '@/utils/analytics'
import { useTranslate } from '@/utils/translate/translate-client'

interface FloatingCallToActionImageProps {
  floatingCta: FloatingCallToActionType
  onClick: () => void
  isGuildMember: boolean
  guildPermissions?: GuildPermissions
}

export const FloatingCallToActionImage: React.FC<FloatingCallToActionImageProps> = ({
  floatingCta,
  onClick,
  isGuildMember,
  guildPermissions,
}) => {
  const { t } = useTranslate('home')
  const [showCta, setShowCta] = useState(true)
  const track = useSafeTrack()

  const handleClick = useCallback(() => {
    track('Floating CTA Clicked')
    onClick()
  }, [onClick, track])

  const handleClose = () => {
    setShowCta(false)
  }

  const seniorExperimentImageUrl = '/v1756138298/angel-studios/landing/Corner%20CTA/The-Senior_Corner-Pin_2.webp'

  return (
    <When condition={showCta}>
      <div className="group-floating-cta fixed inset-x-0 bottom-0 z-40 max-h-[350px] max-w-[350px] gap-4 bg-transparent shortDesktop:max-h-[250px] shortDesktop:max-w-[250px]">
        <div className="relative bg-transparent">
          <When condition={isGuildMember && guildPermissions?.hasFreeTickets}>
            {floatingCta?.guildBackgroundImage?.length ? (
              <GBFadeInExperiment experimentName="senior-corner-cta-experiment" defaultValue={'control'}>
                <ExperimentVariation variation={'variation_1'}>
                  <Image
                    src={getCloudinaryImageUrl({ path: seniorExperimentImageUrl })}
                    alt={floatingCta?.description ?? 'Floating Call to Action'}
                    height={350}
                    className="pointer-events-none cursor-default"
                    width={350}
                    priority
                    style={{
                      transform: 'translateZ(0)',
                      backfaceVisibility: 'hidden',
                      WebkitFontSmoothing: 'antialiased',
                    }}
                  />
                </ExperimentVariation>
                <ExperimentVariation variation={'variation_2'}>
                  <Image
                    src={buildCloudinaryImagePathFromContentfulObject(floatingCta?.guildBackgroundImage)}
                    alt={floatingCta?.description ?? 'Floating Call to Action'}
                    height={350}
                    className="pointer-events-none cursor-default"
                    width={350}
                    priority
                    style={{
                      transform: 'translateZ(0)',
                      backfaceVisibility: 'hidden',
                      WebkitFontSmoothing: 'antialiased',
                    }}
                  />
                </ExperimentVariation>
                <ExperimentVariation variation={'control'}>{null}</ExperimentVariation>
              </GBFadeInExperiment>
            ) : (
              floatingCta?.backgroundImage?.length && (
                <GBFadeInExperiment experimentName="senior-corner-cta-experiment" defaultValue={'control'}>
                  <ExperimentVariation variation={'variation_1'}>
                    <Image
                      src={getCloudinaryImageUrl({ path: seniorExperimentImageUrl })}
                      alt={floatingCta?.description ?? 'Floating Call to Action'}
                      height={350}
                      className="pointer-events-none cursor-default"
                      width={350}
                      priority
                      style={{
                        transform: 'translateZ(0)',
                        backfaceVisibility: 'hidden',
                        WebkitFontSmoothing: 'antialiased',
                      }}
                    />
                  </ExperimentVariation>
                  <ExperimentVariation variation={'variation_2'}>
                    <Image
                      src={buildCloudinaryImagePathFromContentfulObject(floatingCta?.backgroundImage)}
                      alt={floatingCta?.description ?? 'Floating Call to Action'}
                      height={350}
                      className="pointer-events-none cursor-default"
                      width={350}
                      priority
                      style={{
                        transform: 'translateZ(0)',
                        backfaceVisibility: 'hidden',
                        WebkitFontSmoothing: 'antialiased',
                      }}
                    />
                  </ExperimentVariation>
                  <ExperimentVariation variation={'control'}>{null}</ExperimentVariation>
                </GBFadeInExperiment>
              )
            )}
          </When>
          <When condition={!isGuildMember && !guildPermissions?.hasFreeTickets}>
            {floatingCta?.backgroundImage?.length && (
              <GBFadeInExperiment experimentName="senior-corner-cta-experiment" defaultValue={'control'}>
                <ExperimentVariation variation={'variation_1'}>
                  <Image
                    src={getCloudinaryImageUrl({ path: seniorExperimentImageUrl })}
                    alt={floatingCta?.description ?? 'Floating Call to Action'}
                    height={350}
                    className="pointer-events-none cursor-default"
                    width={350}
                    priority
                    style={{
                      transform: 'translateZ(0)',
                      backfaceVisibility: 'hidden',
                      WebkitFontSmoothing: 'antialiased',
                    }}
                  />
                </ExperimentVariation>
                <ExperimentVariation variation={'variation_2'}>
                  <Image
                    src={buildCloudinaryImagePathFromContentfulObject(floatingCta?.backgroundImage)}
                    alt={floatingCta?.description ?? 'Floating Call to Action'}
                    height={350}
                    className="pointer-events-none cursor-default"
                    width={350}
                    priority
                    style={{
                      transform: 'translateZ(0)',
                      backfaceVisibility: 'hidden',
                      WebkitFontSmoothing: 'antialiased',
                    }}
                  />
                </ExperimentVariation>
                <ExperimentVariation variation={'control'}>{null}</ExperimentVariation>
              </GBFadeInExperiment>
            )}
          </When>
          <a
            href={floatingCta?.buttonHref ?? '/tickets'}
            className={'absolute bottom-[2px] left-1 h-10 w-36 cursor-pointer bg-transparent'}
            onClick={handleClick}
            aria-label={t('floatingCta', 'Floating Call to Action')}
          />
          <a
            href={floatingCta?.buttonHref ?? '/tickets'}
            className={
              'absolute -bottom-20 -left-60 h-[262px] w-[600px] rotate-[0.6558rad] cursor-pointer bg-transparent  shortDesktop:h-[195px]'
            }
            onClick={handleClick}
            aria-label={t('floatingCta', 'Floating Call to Action')}
          />
          <div
            className="absolute left-2 top-14 z-[5000] cursor-pointer rounded-full bg-black opacity-0 transition-all duration-300 ease-in-out group-floating-cta-hover:opacity-100 hover:opacity-70"
            onClick={handleClose}
            aria-label={t('closeFloatingCta', 'Close Floating Call to Action')}
          >
            <CircleCloseIcon color="core-gray-500" size={24} className="cursor-pointer" />
          </div>
        </div>
      </div>
    </When>
  )
}
